import React from 'react';
import { OrderWithConfirmation as UIOrderWithConfirmation } from '@shopee/chatbot-ui';
import { OrderWithConfirmationProps } from '@shopee/chatbot-ui/lib/components/OrderWithConfirmation';
import { ButtonGroupProps } from '@shopee/chatbot-ui/lib/components/ButtonGroup';

import { ChatbotUIBasicProps, DIALOGUE_TYPE } from '@/types/common';
import { USER_SEND_TYPE } from '@/types/history';
import { cdnFile } from '@/utils/cdnFile';
import { getTrans } from '@/utils/instantLoaded/tools';
import { updateDialogueInfo, updateInitListDialogueInfo } from '@/utils/sessionUtils';
import { ButtonAction, getButtonAction } from '@/utils/getButtonData';
import { CLARIFICATION_FLAG_YES, INPUT_TYPE, OrderSendScene, TRACKING_MSG_TYPE } from '@/utils/constants';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/tracking/constants';

type Props = OrderWithConfirmationProps & ChatbotUIBasicProps & { id: string; status: string; hideButtons: boolean };

const OrderWithConfirmation: React.FC<Props> = (props) => {
  const { id, status, image, title, statusText, totalPrice, isHistory, hideButtons, customCallback = {} } = props;

  const commonTrackingData = {
    chat_session_id: props.isHistory ? props.historySessionId : props.sessionId,
    chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
    if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
  };

  const noButtonText = getTrans('order_card_no_button') || 'No';
  const yesButtonText = getTrans('order_card_yes_button') || 'Yes';
  const clickFunc = getButtonAction(ButtonAction.JumpNode);
  const clickCallback = (res: any) => {
    if (res) {
      if (props.listPos !== undefined) {
        updateDialogueInfo({ hideButtons: true }, props.listPos);
      } else {
        updateInitListDialogueInfo(DIALOGUE_TYPE.OrderWithConfirmation, { hideButtons: true });
      }
    }
  };

  const buttonGroup = {
    layout: 'horizontal',
    buttons: [
      {
        type: 'primary-outlined',
        text: noButtonText,
        action: {
          func: clickFunc,
          nextParams: {
            session_id: props.sessionId,
            dialogue_id: props.dialogueId,
            input_type: INPUT_TYPE.INPUT_BY_CLICK_ORDER_CONFIRMATION_NO,
            message_text: noButtonText,
            message_extra: { clarification_flag: CLARIFICATION_FLAG_YES },
          },
          callback: clickCallback,
          customCallback,
        },
        trackingData: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_WITH_CONFIRMATION],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_WITH_CONFIRMATION_NO],
          trackingdataBasic: commonTrackingData,
        },
      },
      {
        type: 'primary',
        text: yesButtonText,
        action: {
          func: clickFunc,
          nextParams: {
            session_id: props.sessionId,
            dialogue_id: props.dialogueId,
            input_type: INPUT_TYPE.BUTTON,
            message_text: yesButtonText,
            message_extra: {
              clarification_flag: CLARIFICATION_FLAG_YES,
              params: {
                user_send: {
                  type: USER_SEND_TYPE.TEXT,
                  send_scene: OrderSendScene.LandingPage,
                  text: yesButtonText,
                },
                order_id: id,
              },
            },
          },
          callback: clickCallback,
          customCallback,
        },
        trackingData: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_WITH_CONFIRMATION],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_WITH_CONFIRMATION_YES],
          trackingdataBasic: commonTrackingData,
        },
      },
    ],
  };

  return (
    <UIOrderWithConfirmation
      image={cdnFile(image)}
      title={title}
      statusText={statusText}
      totalPrice={totalPrice}
      showButtons={!isHistory && !hideButtons}
      buttonGroup={buttonGroup as ButtonGroupProps}
      trackingData={{
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_WITH_CONFIRMATION],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_WITH_CONFIRMATION],
        trackingdataBasic: {
          ...commonTrackingData,
          order_id: id,
          order_status: status,
        },
      }}
    />
  );
};

export default OrderWithConfirmation;
